import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, FilePlus2, PenLine, Plus, Send, Trash2, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { aceitoDaUrl, uuidDaUrl } from '../../componentes/filtros-lista';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, nomeDeArquivo, nomeParceiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { FormAssinatura } from './Assinaturas';
import { HashCurto, Linha } from './comum';
import { COR_ASSINATURA, REGISTROS, invalidarDocumentos, tamanhoArquivo, type Documento, type DocumentoDetalhe, type TipoDocumento } from './tipos';

const TIPOS: TipoDocumento[] = ['arquivo', 'contrato'];

/** Sobe o arquivo pelo `/anexos` do núcleo para o documento `documentoId` (ainda pendente) e devolve o id do anexo. */
export async function subirArquivo(documentoId: string, arquivo: File) {
  const fd = new FormData();
  fd.append('entidade', 'documentos.documento');
  fd.append('registro_id', documentoId);
  fd.append('arquivo', arquivo);
  return (await api<{ id: string }>('POST', '/anexos', fd)).id;
}

/** Rótulo do registro ligado: o nome do parceiro quando o sistema sabe; senão o tipo do registro. */
export function useRotuloRegistro() {
  const { t } = useTranslation('documentos');
  return (d: Pick<Documento, 'registro_tipo' | 'registro_titulo'>) => {
    if (!d.registro_tipo) return t('semRegistro');
    if (d.registro_titulo) return nomeParceiro(d.registro_titulo);
    return REGISTROS.some((r) => r.tipo === d.registro_tipo) ? t(`registros.${d.registro_tipo}`) : t('registros.outro');
  };
}

/** Lista de documentos (Arquivos e, com `tipo`, a lista de contratos gerados). Abre pela URL com `?id=`. */
export function ListaDocumentos({ tipo, tela, vazio, acao }: { tipo?: TipoDocumento; tela: string; vazio: string; acao?: ReactNode }) {
  const { t } = useTranslation('documentos');
  const [busca, setBusca] = useSearchParams();
  const rotulo = useRotuloRegistro();
  const [filtroTipo, setFiltroTipo] = useState<TipoDocumento | ''>(tipo ?? (aceitoDaUrl(busca.get('tipo'), TIPOS) as TipoDocumento | ''));
  const [aberto, setAberto] = useState<string | null>(uuidDaUrl(busca.get('id')) || null);
  useEffect(() => {
    const id = uuidDaUrl(busca.get('id'));
    if (id) setAberto(id);
  }, [busca]);
  const fechar = () => {
    setAberto(null);
    if (busca.get('id')) setBusca((b: URLSearchParams) => (b.delete('id'), b), { replace: true });
  };
  const colunas: Coluna<Documento>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, render: (d) => <span className="font-medium">{textoLivre(d.nome, d.registro_titulo)}</span> },
    { chave: 'registro_titulo', rotulo: t('campos.ligadoA'), render: (d) => rotulo(d) },
    ...(tipo ? [] : [{ chave: 'tipo', rotulo: t('campos.tipo'), render: (d: Documento) => t(`tipo.${d.tipo}`) } as Coluna<Documento>]),
    { chave: 'versao', rotulo: t('campos.versao'), mono: true, render: (d) => t('versaoN', { versao: d.versao }) },
    { chave: 'tags', rotulo: t('campos.tags'), oculta: true, render: (d) => (d.tags.length ? d.tags.map((x) => textoLivre(x)).join(', ') : '-') },
    { chave: 'assinatura_estado', rotulo: t('campos.assinatura'), render: (d) => (d.assinatura_estado ? <Selo cor={COR_ASSINATURA[d.assinatura_estado]}>{t(`estado.${d.assinatura_estado}`)}</Selo> : '-') },
    { chave: 'updated_at', rotulo: t('campos.atualizado'), render: (d) => data(d.updated_at) },
  ];
  const params = useMemo(() => (filtroTipo ? { tipo: filtroTipo } : {}), [filtroTipo]);
  return (
    <>
      {acao}
      <Tabela<Documento>
        tela={tela}
        caminho="/documentos/documentos"
        params={params}
        chaveConsulta={['documentos', 'lista', tela, filtroTipo]}
        colunas={colunas}
        aoClicar={(d) => setAberto(d.id)}
        buscaNaUrl
        filtros={tipo ? undefined : { tipo: filtroTipo }}
        aoAplicarFiltros={tipo ? undefined : (f) => setFiltroTipo(aceitoDaUrl(String(f.tipo ?? ''), TIPOS) as TipoDocumento | '')}
        filtrosExtra={
          tipo ? undefined : (
            <Selecao
              aria-label={t('campos.tipo')}
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value as TipoDocumento | '')}
              opcoes={[{ valor: '', rotulo: t('estado.todos') }, ...TIPOS.map((x) => ({ valor: x, rotulo: t(`tipo.${x}`) }))]}
            />
          )
        }
        vazio={<Vazio texto={vazio} />}
      />
      {aberto && <FichaDocumento id={aberto} aoFechar={fechar} />}
    </>
  );
}

export function Arquivos() {
  const { t } = useTranslation('documentos');
  const s = useSessao();
  const [novo, setNovo] = useState(false);
  return (
    <>
      <ListaDocumentos
        tela="documentos.arquivos"
        vazio={t('vazio.arquivos')}
        acao={
          s.pode('documentos.criar') && (
            <AcaoPrimaria>
              <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
                {t('acoes.anexar')}
              </Botao>
            </AcaoPrimaria>
          )
        }
      />
      {novo && <FormAnexar aoFechar={() => setNovo(false)} />}
    </>
  );
}

/** Seletor de arquivo: o input `sr-only` fica fora do Tab (regra r16); o botão visível é o controle. */
export function EscolherArquivo({ arquivo, aoEscolher, rotulo, aceitar }: { arquivo: File | null; aoEscolher: (f: File | null) => void; rotulo: string; /** D24: tipos aceitos no seletor (o .docx do modelo). */ aceitar?: string }) {
  const { t } = useTranslation('documentos');
  const seletor = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-1.5 sm:col-span-6">
      <span className="text-sm font-medium text-tinta-2">{rotulo}</span>
      <div className="relative flex min-w-0 flex-wrap items-center gap-3">
        <Botao icone={<Upload className="size-4" />} onClick={() => seletor.current?.click()}>
          {arquivo ? t('acoes.trocarArquivo') : t('acoes.escolherArquivo')}
        </Botao>
        {arquivo && (
          <span className="min-w-0 truncate text-sm text-tinta-2" title={arquivo.name}>
            {nomeDeArquivo(arquivo.name)}
          </span>
        )}
        <input ref={seletor} type="file" accept={aceitar} className="sr-only" tabIndex={-1} aria-label={rotulo} title={rotulo} onChange={(e) => aoEscolher(e.target.files?.[0] ?? null)} />
      </div>
    </div>
  );
}

/** Anexar: o arquivo sobe antes pelo `/anexos` (com o id que o documento vai ter) e o documento nasce ligado ao registro. */
function FormAnexar({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => crypto.randomUUID(), []);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const enviado = useRef<string | null>(null);
  const registros = REGISTROS.filter((r) => s.instalado(r.modulo) && s.pode(`${r.modulo}.ver`));
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', ajuda: t('campos.nomeAjuda') },
    {
      nome: 'registro_tipo',
      rotulo: t('campos.ligadoA'),
      tipo: 'selecao',
      largura: 'meia',
      opcoes: [{ valor: '', rotulo: t('registros.nenhum') }, ...registros.map((r) => ({ valor: r.tipo, rotulo: t(`registros.${r.tipo}`) }))],
    },
    ...registros.map(
      (r) =>
        ({
          nome: `registro_${r.tipo.replace('.', '_')}`,
          rotulo: t(`registros.${r.tipo}`),
          tipo: 'busca',
          largura: 'meia',
          obrigatorio: true,
          visivel: (v: Record<string, unknown>) => v.registro_tipo === r.tipo,
          busca: { caminho: r.caminho, rotulo: (i: Record<string, unknown>) => String(i.nome ?? ''), pessoa: r.tipo === 'pessoas.funcionario' },
        }) as DefCampo,
    ),
    { nome: 'tags', rotulo: t('campos.tags'), tipo: 'tags' },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('acoes.anexar')}
      rodape={
        <Botao variante="primario" type="submit" form="form-documento" icone={<FilePlus2 className="size-4" />}>
          {t('acoes.anexar')}
        </Botao>
      }
    >
      <p className="mb-3 text-sm text-tinta-3">{t('ajuda.anexar')}</p>
      <Formulario
        id="form-documento"
        campos={campos}
        inicial={{ registro_tipo: '', tags: [] }}
        aoEnviar={async (v) => {
          if (!arquivo) {
            toast.erro(t('arquivoObrigatorio'));
            return;
          }
          enviado.current ??= await subirArquivo(id, arquivo);
          const tipo = String(v.registro_tipo ?? '');
          const registroId = tipo ? String(v[`registro_${tipo.replace('.', '_')}`] ?? '') : '';
          await post(
            '/documentos/documentos',
            { id, anexo_id: enviado.current, nome: String(v.nome ?? '').trim() || undefined, ...(tipo && registroId ? { registro_tipo: tipo, registro_id: registroId } : {}), tags: Array.isArray(v.tags) ? v.tags : [] },
            { 'Idempotency-Key': id },
          );
          await invalidarDocumentos(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      >
        <EscolherArquivo
          arquivo={arquivo}
          rotulo={t('campos.arquivo')}
          aoEscolher={(f) => {
            enviado.current = null;
            setArquivo(f);
          }}
        />
      </Formulario>
    </Drawer>
  );
}



/** Ficha do documento: dados, versões (com o hash de cada arquivo) e assinaturas; editar, nova versão e enviar. */
export function FichaDocumento({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const rotulo = useRotuloRegistro();
  const seletor = useRef<HTMLInputElement>(null);
  const [editando, setEditando] = useState(false);
  const [remover, setRemover] = useState(false);
  const [assinar, setAssinar] = useState(false);
  const q = useQuery({ queryKey: ['documentos', 'documento', id], queryFn: () => get<DocumentoDetalhe>(`/documentos/documentos/${id}`) });
  const versao = useMutation({
    mutationFn: async (arquivo: File) => post(`/documentos/documentos/${id}/versoes`, { anexo_id: await subirArquivo(id, arquivo) }),
    onSuccess: async () => {
      await invalidarDocumentos(qc);
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const excluir = useMutation({
    mutationFn: () => del(`/documentos/documentos/${id}`),
    onSuccess: async () => {
      await invalidarDocumentos(qc);
      aoFechar();
    },
    onError: (e) => {
      setRemover(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const d = q.data;
  const emAssinatura = d?.assinaturas.some((a) => a.estado === 'enviado');
  const titulo = d ? textoLivre(d.nome, d.registro_titulo) : t('campos.documento');
  return (
    <>
      <Drawer
        aberto={!editando && !assinar}
        aoFechar={aoFechar}
        largura="lg"
        titulo={titulo}
        rodape={
          d ? (
            <>
              {s.pode('documentos.excluir') && (
                <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(true)}>
                  {t('acoes.excluir')}
                </Botao>
              )}
              {s.pode('documentos.editar') && (
                <>
                  <Botao icone={<PenLine className="size-4" />} onClick={() => setEditando(true)}>
                    {t('acoes.editar')}
                  </Botao>
                  <Botao icone={<Upload className="size-4" />} carregando={versao.isPending} aria-disabled={emAssinatura} onClick={() => !emAssinatura && seletor.current?.click()}>
                    {t('acoes.novaVersao')}
                  </Botao>
                </>
              )}
              {s.pode('documentos.criar') && (
                <Botao variante="primario" icone={<Send className="size-4" />} onClick={() => setAssinar(true)}>
                  {t('acoes.enviarAssinatura')}
                </Botao>
              )}
            </>
          ) : (
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
          )
        }
      >
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : d ? (
          <div className="relative flex flex-col gap-5">
            <dl>
              <Linha rotulo={t('campos.tipo')}>{t(`tipo.${d.tipo}`)}</Linha>
              <Linha rotulo={t('campos.ligadoA')}>{rotulo(d)}</Linha>
              <Linha rotulo={t('campos.versao')}>
                <a className="text-acento-forte hover:underline" download href={`/api/v1/anexos/${d.anexo_id}`}>
                  {t('versaoN', { versao: d.versao })} · {nomeDeArquivo(d.arquivo_nome, d.registro_titulo)} · {tamanhoArquivo(d.tamanho)}
                </a>
              </Linha>
              <Linha rotulo={t('campos.tags')}>{d.tags.length ? d.tags.map((x) => textoLivre(x)).join(', ') : '-'}</Linha>
              <Linha rotulo={t('campos.criadoPor')}>
                {nomePessoa(d.criado_por_nome) || '-'} · {dataHora(d.created_at)}
              </Linha>
            </dl>
            <section>
              <h3 className="mb-1 text-sm font-semibold text-tinta-1">{t('campos.versoes')}</h3>
              <p className="mb-2 text-xs text-tinta-3">{t('ajuda.versao')}</p>
              <ul className="flex flex-col divide-y divide-borda">
                {d.versoes.map((v) => (
                  <li key={v.versao} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block font-medium text-tinta-1">
                        {t('versaoN', { versao: v.versao })} · {nomeDeArquivo(v.nome, d.registro_titulo)}
                      </span>
                      <span className="block text-tinta-3">
                        {dataHora(v.created_at)} · {tamanhoArquivo(v.tamanho)} · {t('campos.hash')} <HashCurto hash={v.sha256} />
                      </span>
                    </span>
                    <a className="inline-flex items-center gap-1 text-acento-forte hover:underline" download href={`/api/v1/anexos/${v.anexo_id}`} aria-label={t('acoes.baixarVersao', { versao: v.versao })}>
                      <Download className="size-4" aria-hidden />
                      {t('acoes.baixar')}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
            {d.assinaturas.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-tinta-1">{t('campos.assinaturas')}</h3>
                <ul className="flex flex-col divide-y divide-borda">
                  {d.assinaturas.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span className="min-w-min">
                        <Selo cor={COR_ASSINATURA[a.estado]}>{t(`estado.${a.estado}`)}</Selo>
                      </span>
                      <span className="grow basis-24 text-tinta-3">
                        {t('versaoN', { versao: a.versao })} · {dataHora(a.created_at)}
                      </span>
                      <Link className="text-acento-forte hover:underline" to={`/cadastros/documentos/assinaturas?id=${a.id}`}>
                        {t('acoes.abrirAssinatura')}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <input
              ref={seletor}
              type="file"
              className="sr-only"
              tabIndex={-1}
              aria-label={t('acoes.novaVersao')}
              title={t('acoes.novaVersao')}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) versao.mutate(f);
              }}
            />
          </div>
        ) : null}
      </Drawer>
      {d && editando && <EditarDocumento documento={d} aoFechar={() => setEditando(false)} />}
      {d && assinar && <FormAssinatura documento={{ id: d.id, nome: d.nome, tipo: d.tipo, registro_tipo: d.registro_tipo, registro_id: d.registro_id }} aoFechar={() => setAssinar(false)} />}
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => excluir.mutate()} titulo={t('confirmar.excluir')} texto={t('confirmar.excluirTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}

function EditarDocumento({ documento, aoFechar }: { documento: DocumentoDetalhe; aoFechar: () => void }) {
  const { t } = useTranslation('documentos');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, nomes: [documento.registro_titulo] },
    { nome: 'tags', rotulo: t('campos.tags'), tipo: 'tags' },
  ];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.editar')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-editar-documento">
            {t('acoes.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-editar-documento"
        campos={campos}
        inicial={{ nome: documento.nome, tags: documento.tags }}
        aoEnviar={async (v) => {
          await patch(`/documentos/documentos/${documento.id}`, { nome: v.nome, tags: Array.isArray(v.tags) ? v.tags : [] });
          await invalidarDocumentos(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Modal>
  );
}
